// Short lessons that use the real sky: each step points at something that is up (or says where it
// is), asks the learner to find it, or checks understanding with a question. Written for Ahillah;
// facts are kept to well-established values, and anything that changes (the Moon, planets, passes,
// dates) is worked out live.

import { settings } from '../state';

export type Target =
  | { star: string }
  | { body: string }
  | { con: string } // IAU abbreviation
  | { dso: string } // e.g. 'M45'
  | { sat: number }
  | { shower: string }
  | { dir: number; alt: number }; // compass bearing and height, degrees

export interface LessonCtx {
  lat: number;
  placeName: string;
  /** Height above the horizon now, in degrees (null if unknown). */
  alt(t: Target): number | null;
  /** "high in the south-west", or "below the horizon". */
  where(t: Target): string;
  /** The first of these that is at least 10° up, if any. */
  firstUp(ts: Target[]): Target | null;
  /** On the 29th, the 30th and the 1st, phase "Crescent" and no `pct`; through the 29th, `looking`, all there is to
   *  say: that Saudi Arabia looks for the crescent (src/live/saudi.ts). `nextNew` is null when it falls on one of
   *  those days, as the sky calendar names no New Moon on them. */
  moon: { phase: string; pct: number | null; up: boolean; nextFull: Date; nextNew: Date | null; looking?: string };
  /** Bright planets that are up now, brightest first. */
  planetsUp: { id: string; mag: number }[];
  bortle: number;
  nelm: number;
  /** The stars the eye picks out at once on a clear night with no Moon, as Naked eye counts them: here, and under a
   *  truly dark sky (src/render/nakedeye.ts). */
  starsHere: number;
  starsDark: number;
  nextShower: { name: string; code: string; date: Date; zhr: number } | null;
  issPass: { t: number; fromDir: string; peakEl: number } | null;
  nextSolarEclipse: { date: Date; obscuration: number } | null;
  fmtDate(d: Date | number): string;
  fmtTime(d: Date | number): string;
}

export type Text = string | ((c: LessonCtx) => string);

export type Step =
  /** `quotes`: approved texts shown word for word under the text (islamEvidence.ts); `islam`: a section of "The stars in
   *  Islam" the step offers to open. */
  | { look: Target | ((c: LessonCtx) => Target | null) | null; title?: string; text: Text; fov?: number; layers?: Record<string, unknown>; quotes?: string[]; islam?: string }
  | { find: Target | ((c: LessonCtx) => Target | null); title?: string; text: Text; radius?: number }
  | { ask: Text; options: string[]; answer: number; explain: Text };

export interface Lesson {
  id: string;
  title: string;
  level: 'Everyone' | 'Explorer' | 'Navigator';
  minutes: number;
  summary: string;
  north?: boolean; // only makes sense in the northern hemisphere
  steps: Step[];
}

const brightestPlanet = (c: LessonCtx): Target | null => (c.planetsUp[0] ? { body: c.planetsUp[0].id } : null);
const RED = [{ star: 'Betelgeuse' }, { star: 'Aldebaran' }, { star: 'Arcturus' }, { star: 'Antares' }];
const BLUE = [{ star: 'Rigel' }, { star: 'Vega' }, { star: 'Regulus' }, { star: 'Spica' }, { star: 'Sirius' }];
/** "It is full Moon", "The Moon is at first quarter", "The Moon is a waxing crescent". */
const moonPhrase = (phase: string) => {
  const p = phase.toLowerCase();
  if (/moon/.test(p)) return `It is ${p.replace(/ moon$/, '')} Moon`;
  return `The Moon is ${/quarter/.test(p) ? 'at ' : /crescent/.test(p) ? 'a ' : ''}${p}`;
};
/**
 * Published surface temperatures (kelvin, the main star of each system) and the colour the eye sees:
 * Betelgeuse, Levesque & Massey 2020; Antares, Ohnaka et al. 2013; Aldebaran, Heiter et al. 2015;
 * Arcturus, Ramírez & Allende Prieto 2011; Rigel, Przybilla et al. 2010; Vega, Monnier et al. 2012 and
 * Regulus, Che et al. 2011 (both fast spinners, hotter at the poles; the value as seen from Earth);
 * Spica, Tkachenko et al. 2016; Sirius, Davis et al. 2011.
 */
const STAR_TEMP: Record<string, { k: number; colour: string }> = {
  Betelgeuse: { k: 3600, colour: 'orange-red' },
  Antares: { k: 3660, colour: 'orange-red' },
  Aldebaran: { k: 3927, colour: 'orange' },
  Arcturus: { k: 4286, colour: 'orange' },
  Rigel: { k: 12100, colour: 'blue-white' },
  Vega: { k: 9660, colour: 'blue-white' },
  Regulus: { k: 12080, colour: 'blue-white' },
  Spica: { k: 25300, colour: 'blue-white' },
  Sirius: { k: 9845, colour: 'blue-white' },
};
const tempC = (name: string) => (Math.round((STAR_TEMP[name].k - 273) / 100) * 100).toLocaleString('en-GB');
const nameOf = (t: Target | null) => (t && 'star' in t ? t.star : t && 'body' in t ? t.body : '');

export const LESSONS: Lesson[] = [
  {
    id: 'north',
    title: 'Find north with the Plough',
    level: 'Everyone',
    minutes: 3,
    summary: 'Use two stars of the Plough to find the Pole Star, and with it north.',
    north: true,
    steps: [
      {
        look: { con: 'UMa' },
        fov: 70,
        title: 'The Plough',
        text: (c) =>
          `These seven bright stars are the Plough, part of the constellation Ursa Major, the Great Bear. It is ${c.where({ con: 'UMa' })}. ` +
          (c.lat > 41 ? 'From here it never sets: it circles the Pole Star all night, all year.' : 'From here it dips below the horizon for part of each day.'),
      },
      {
        look: { star: 'Dubhe' },
        fov: 55,
        title: 'The Pointers',
        text: 'The two stars at the end of the pan, Merak and Dubhe, are called the Pointers. Follow the line from Merak through Dubhe, away from the pan, for about five times the gap between them.',
      },
      { find: { star: 'Polaris' }, title: 'Find the Pole Star', text: 'Now find Polaris, the Pole Star, and put it in the circle.' },
      {
        look: { star: 'Polaris' },
        fov: 60,
        title: 'North',
        text: (c) =>
          `Polaris is ${Math.round(c.alt({ star: 'Polaris' }) ?? c.lat)}° above the horizon here, almost exactly your latitude (${Math.round(c.lat)}° north). Face it and you are facing north. It is not especially bright, only about the fiftieth brightest star, but it hardly moves: the whole sky turns around it.`,
      },
      // The stars as guides in the Quran (the owner's A14, 14 October 2026).
      { look: null, title: 'By the stars they find their way', text: '', quotes: ['Q-16:16'] },
      {
        ask: 'Polaris is 30° above your horizon. About how far north are you?',
        options: ['At the equator', '30° north', '60° north', 'At the North Pole'],
        answer: 1,
        explain: 'The Pole Star’s height above the horizon is your latitude. Sailors used this for centuries to work out how far north they were.',
      },
    ],
  },
  {
    id: 'turning',
    title: 'Why the sky turns',
    level: 'Everyone',
    minutes: 3,
    summary: 'The stars wheel around the pole because Earth spins. Some never set.',
    north: true,
    steps: [
      {
        look: (c) => ({ dir: 0, alt: Math.max(15, Math.min(70, c.lat)) }),
        fov: 100,
        title: 'Around the pole',
        text: 'Over a night, everything in the sky seems to turn around a point very close to Polaris. It is really Earth turning, about once a day, carrying us with it.',
      },
      {
        look: { con: 'Cas' },
        fov: 70,
        title: 'Cassiopeia and the Plough',
        text: (c) => `Cassiopeia, the W shape, sits on the other side of the Pole Star from the Plough. Right now it is ${c.where({ con: 'Cas' })}. The two take turns being high and low through the night.`,
      },
      {
        look: { dir: 90, alt: 15 },
        fov: 100,
        title: 'Rising in the east',
        text: 'Stars rise in the eastern half of the sky and set in the western half, like the Sun, because Earth spins from west to east. Near the pole, stars never set at all: astronomers call them circumpolar.',
      },
      {
        ask: 'How long do the stars take to go round once?',
        options: ['Exactly 24 hours', 'About 23 hours 56 minutes', '12 hours', 'A week'],
        answer: 1,
        explain: 'About four minutes less than a day, because Earth also moves along its orbit. So each star rises about four minutes earlier every night, and the evening sky changes with the seasons.',
      },
      { ask: 'Where do stars rise?', options: ['In the north', 'Somewhere in the east', 'In the south', 'In the west'], answer: 1, explain: 'Somewhere along the eastern horizon, like the Sun and the Moon: stars far to the north rise towards the north, those far to the south towards the south. Stars near the pole never set at all.' },
    ],
  },
  {
    id: 'moon',
    title: 'The Moon’s phases',
    level: 'Everyone',
    minutes: 3,
    summary: 'Why the Moon changes shape, and when it will next be full.',
    steps: [
      {
        // Not at the Moon through the 29th, when nothing says where it is (src/live/saudi.ts).
        look: (c) => (c.moon.looking ? null : { body: 'Moon' }),
        fov: 12,
        title: 'Tonight’s Moon',
        text: (c) => c.moon.looking ?? `${moonPhrase(c.moon.phase)}${c.moon.pct === null ? '' : `, ${c.moon.pct}% lit`}${c.moon.up ? `, ${c.where({ body: 'Moon' })}` : ', but below the horizon right now'}.`,
      },
      {
        look: (c) => (c.moon.looking ? null : { body: 'Moon' }),
        fov: 12,
        title: 'Lit by the Sun',
        text: 'The Moon makes no light of its own. The half facing the Sun is always lit; its phase is how much of that sunlit half faces us as it goes round Earth.',
      },
      // The new crescents in the Quran (the owner's A14, 14 October 2026), as "Why Ahillah?" gives the verse.
      { look: null, title: 'Markers of time', text: '', quotes: ['Q-2:189'] },
      {
        ask: 'How long is it from one full Moon to the next?',
        options: ['About a week', 'About 29½ days', 'Exactly one calendar month', 'A year'],
        answer: 1,
        explain: (c) => `About 29.5 days on average; the word “month” comes from the Moon. The next full Moon is on ${c.fmtDate(c.moon.nextFull)}${c.moon.pct === null || !c.moon.nextNew ? '' : `, and the next new Moon on ${c.fmtDate(c.moon.nextNew)}`}.`,
      },
      {
        ask: 'Why do we always see the same side of the Moon?',
        options: ['It does not spin', 'It spins exactly once each time it goes round Earth', 'The other side is always dark', 'Clouds hide the far side'],
        answer: 1,
        explain: 'It turns once for every trip round Earth, so the same face always points at us. The far side is not dark: it gets just as much sunlight over a month.',
      },
    ],
  },
  // Written to Saudi Arabia's method (BUILD-SPEC item 44), as the owner approved it on 14 October 2026 (A13): every Islamic
  // sentence is a quoted, approved text; Ahillah's own words only describe the sky and the court's practice.
  {
    id: 'crescent',
    title: 'The crescent and the Hijri month',
    level: 'Everyone',
    minutes: 3,
    summary: 'How the Moon marks the months, and how each new month begins.',
    steps: [
      {
        // At the Moon if it is up, but not through the 29th, when nothing says where it is (src/live/saudi.ts).
        look: (c) => (c.moon.up && !c.moon.looking ? { body: 'Moon' } : null),
        fov: 12,
        title: 'The Moon’s stations',
        text: 'Each night the Moon moves on among the stars, from a thin crescent to full and back again.',
        quotes: ['Q-49'],
      },
      { look: null, title: 'For counting the years', text: '', quotes: ['Q-48'] },
      { look: null, title: 'Twenty-nine or thirty days', text: 'From one new Moon to the next takes about twenty-nine and a half days.', quotes: ['H-42'] },
      { look: null, title: 'When you see it', text: '', quotes: ['H-41', 'SC-47'] },
      {
        look: null,
        title: 'How the month begins in Saudi Arabia',
        text: 'On the evening of the 29th, Saudi Arabia’s Supreme Court asks people to look for the new crescent. If it is seen, the new month begins the next day. If it is not, the month completes thirty days.',
      },
      { look: { dir: 270, alt: 4 }, fov: 70, title: 'Look for it yourself', text: 'On the evening of the 29th, look low in the west just after sunset.', quotes: ['H-27'] },
      {
        ask: 'True or false? A Hijri month always has thirty days.',
        options: ['True', 'False'],
        answer: 1,
        explain: 'False: the month is “sometimes twenty-nine and sometimes thirty” (Ṣaḥīḥ al-Bukhārī 1913).',
      },
      {
        ask: 'True or false? If the crescent is not seen on the evening of the 29th, the month completes thirty days.',
        options: ['True', 'False'],
        answer: 0,
        explain: 'True: if it is not seen, the month completes thirty days.',
      },
    ],
  },
  {
    id: 'planets',
    title: 'Planets wander',
    level: 'Everyone',
    minutes: 3,
    summary: 'How to tell a planet from a star, and why planets move.',
    steps: [
      {
        look: brightestPlanet,
        fov: 40,
        title: 'A planet tonight',
        text: (c) =>
          c.planetsUp[0]
            ? `${c.planetsUp[0].id} is ${c.where({ body: c.planetsUp[0].id })}. Planets shine by reflecting sunlight, and they usually shine steadily instead of twinkling.`
            : 'No bright planet is up right now. Try this lesson on another evening; the Tonight page lists when each planet is best.',
      },
      {
        look: brightestPlanet,
        fov: 100,
        layers: { ecliptic: true },
        title: 'The Sun’s path',
        text: '“Planet” comes from the Greek for wanderer: over weeks, planets drift against the stars. They always stay close to this line, the ecliptic, the Sun’s path through the sky, because all the planets orbit in nearly the same flat plane.',
      },
      { find: brightestPlanet, title: 'Find it', text: 'Find the planet and put it in the circle.' },
      {
        ask: 'Why do planets twinkle less than stars?',
        options: ['They are close enough to show a tiny disc, not a point', 'They are hotter', 'They are made of rock', 'They move faster'],
        answer: 0,
        explain: 'Stars are so far away that they are points of light, easily jostled by moving air. A planet’s tiny disc averages that out, so it shines more steadily.',
      },
    ],
  },
  {
    id: 'colours',
    title: 'Star colours',
    level: 'Explorer',
    minutes: 3,
    summary: 'A star’s colour tells you how hot it is.',
    steps: [
      {
        look: (c) => c.firstUp(RED) ?? RED[0],
        fov: 30,
        title: 'A cool, orange star',
        text: (c) => {
          const up = c.firstUp(RED);
          const n = nameOf(up ?? RED[0]);
          return `${up ? 'Look closely: stars' : 'Stars'} have colours. ${n}${up ? '' : ', below the horizon just now,'} is ${STAR_TEMP[n].colour}, about ${tempC(n)} °C at its surface. That is cool for a star.`;
        },
      },
      {
        look: (c) => c.firstUp(BLUE) ?? BLUE[0],
        fov: 30,
        title: 'A hot, blue-white star',
        text: (c) => {
          const up = c.firstUp(BLUE);
          const n = nameOf(up ?? BLUE[0]);
          return `${n}${up ? '' : ', below the horizon just now,'} is blue-white, about ${tempC(n)} °C. Our Sun, at about 5,500 °C, lies in between: its light is white, though it looks yellowish through the air.`;
        },
      },
      {
        ask: 'Which stars are the hottest?',
        options: ['Red ones', 'Yellow ones', 'Blue-white ones', 'They are all the same'],
        answer: 2,
        explain: 'Like metal in a fire, a star glows red when cooler and white to blue when hotter. Colours are easier to see through binoculars.',
      },
    ],
  },
  {
    id: 'distance',
    title: 'How far is far?',
    level: 'Explorer',
    minutes: 3,
    summary: 'Light-years, and the most distant thing most people can see by eye.',
    steps: [
      {
        look: { star: 'Vega' },
        fov: 40,
        title: 'Vega',
        text: 'Vega is about 25 light-years away. A light-year is the distance light travels in a year, about 9.5 trillion kilometres.',
      },
      {
        look: { star: 'Sirius' },
        fov: 40,
        title: 'Sirius',
        text: (c) => `Sirius, the brightest star in the night sky, is one of our nearest neighbours: about 8.6 light-years away. It is ${c.where({ star: 'Sirius' })} right now.`,
      },
      {
        look: { dso: 'M31' },
        fov: 20,
        title: 'The Andromeda Galaxy',
        text: 'The Andromeda Galaxy is a spiral of roughly a trillion stars, about 2.5 million light-years away. From a dark site it shows by eye as a faint, oval smudge.',
      },
      { ask: 'A light-year is a measure of…', options: ['time', 'distance', 'brightness', 'speed'], answer: 1, explain: 'Distance: how far light goes in a year.' },
    ],
  },
  {
    id: 'constellations',
    title: 'What is a constellation?',
    level: 'Everyone',
    minutes: 2,
    summary: 'Patterns, official areas of sky, and asterisms like the Plough.',
    steps: [
      {
        look: (c) => c.firstUp([{ con: 'Ori' }, { con: 'Cas' }, { con: 'UMa' }, { con: 'Cyg' }, { con: 'Leo' }, { con: 'Sco' }]) ?? { con: 'UMa' },
        fov: 70,
        title: 'Patterns in the stars',
        text: 'A constellation is a group of stars that makes a pattern, joined here with lines. The patterns make it easier to find your way around the sky and to say where something is.',
      },
      {
        look: (c) => c.firstUp([{ con: 'Ori' }, { con: 'Cas' }, { con: 'UMa' }, { con: 'Cyg' }]) ?? { con: 'UMa' },
        fov: 90,
        layers: { constellationBounds: true },
        title: '88 areas of sky',
        text: 'Astronomers divide the whole sky into 88 official constellations, each an area with fixed borders, like countries on a map. Every star belongs to one of them.',
      },
      {
        ask: 'Is the Plough a constellation?',
        options: ['Yes, one of the 88', 'No: it is a smaller pattern within Ursa Major'],
        answer: 1,
        explain: 'The Plough is an asterism, a well-known pattern that is part of a constellation (Ursa Major, the Great Bear).',
      },
    ],
  },
  {
    id: 'meteors',
    title: 'Shooting stars',
    level: 'Everyone',
    minutes: 3,
    summary: 'What meteors are, and how to watch a meteor shower.',
    steps: [
      {
        look: (c) => (c.nextShower ? { shower: c.nextShower.code } : null),
        fov: 100,
        title: 'Specks of dust',
        text: 'A shooting star, or meteor, is a speck of dust, often smaller than a grain of sand, hitting the air at tens of kilometres a second and burning up about 100 km above the ground.',
      },
      {
        look: (c) => (c.nextShower ? { shower: c.nextShower.code } : null),
        fov: 100,
        title: 'Meteor showers',
        text: (c) =>
          c.nextShower
            ? `During a shower, meteors seem to fly out of one point, the radiant. The next ${c.nextShower.zhr >= 50 ? 'big one' : 'one worth watching'} is the ${c.nextShower.name}, peaking on ${c.fmtDate(c.nextShower.date)}, with up to ${c.nextShower.zhr} an hour under perfect, dark skies (fewer in practice).`
            : 'During a shower, meteors seem to fly out of one point, the radiant, as Earth ploughs through a stream of dust.',
      },
      {
        ask: 'What are most meteor showers made of?',
        options: ['Dust left behind by comets', 'Bits of old satellites', 'Stars falling to Earth', 'Hailstones'],
        answer: 0,
        explain: 'Comets shed dust along their orbits. When Earth crosses one of these trails each year, we get a shower. The Geminids are unusual: their dust comes from an asteroid, Phaethon.',
      },
      {
        ask: 'Where should you look during a meteor shower?',
        options: ['Only straight at the radiant', 'Anywhere, about halfway up, with a wide view', 'Only near the horizon', 'Through a telescope'],
        answer: 1,
        explain: 'Meteors can appear anywhere. Lie back, take in as much sky as you can, and give your eyes at least 20 to 30 minutes to adjust to the dark.',
      },
      // The verse with Ibn Kathīr's short explanation, and the way to the article (BUILD-SPEC item 14).
      { look: null, title: 'Shooting stars in Islam', text: '', quotes: ['Q-40', 'SC-25'], islam: 'shooting-stars' },
    ],
  },
  {
    id: 'iss',
    title: 'Spot the space station',
    level: 'Everyone',
    minutes: 2,
    summary: 'Why the ISS shines, and your next chance to see it.',
    steps: [
      {
        look: { sat: 25544 },
        fov: 100,
        title: 'The International Space Station',
        text: 'The ISS is about 400 km up and goes round Earth about every 92 minutes, at nearly 28,000 km an hour. It has no bright lights of its own: you see it because it reflects sunlight while the ground below you is already dark.',
      },
      {
        look: { sat: 25544 },
        fov: 100,
        title: 'Your next chance',
        text: (c) =>
          c.issPass
            ? `Next bright pass from ${c.placeName}: ${c.fmtDate(c.issPass.t)} at ${c.fmtTime(c.issPass.t)}, starting in the ${c.issPass.fromDir} and climbing to ${Math.round(c.issPass.peakEl)}°. It looks like a steady, bright star gliding across the sky, taking a few minutes.`
            : 'There is no bright pass in the next few days. Passes come in runs of a week or two; the Tonight page and ISS alerts in Settings will tell you when.',
      },
      {
        ask: 'Why does the ISS sometimes fade away halfway across the sky?',
        options: ['It switches its lights off', 'It passes into Earth’s shadow', 'It goes behind a dust cloud', 'It speeds up'],
        answer: 1,
        explain: 'It moves into Earth’s shadow, where no sunlight reaches it, so it vanishes even though it is still above the horizon.',
      },
    ],
  },
  {
    id: 'dark',
    title: 'Dark skies',
    level: 'Explorer',
    minutes: 3,
    summary: 'How many stars you can see from here, and how to see more.',
    steps: [
      {
        look: { dir: 180, alt: 60 },
        fov: 100,
        title: 'Your sky',
        text: (c) =>
          c.nelm >= 6.3
            ? `Your sky is about Bortle ${c.bortle} on the nine-step scale of sky darkness: truly dark. On a clear, moonless night you might see about ${c.starsHere.toLocaleString('en-GB')} stars from here, close to the most anyone can see without a telescope.`
            : `Your sky is about Bortle ${c.bortle} on the nine-step scale of sky darkness. On a clear, moonless night you might see about ${c.starsHere.toLocaleString('en-GB')} stars from here. Under a truly dark sky there are around ${c.starsDark.toLocaleString('en-GB')}.`,
      },
      {
        ask: 'What helps your eyes see faint stars?',
        options: ['Checking your phone now and then', 'Twenty minutes in the dark, using only red light', 'Staring straight at the faint star', 'A bright torch'],
        answer: 1,
        explain: 'Eyes adapt well in 20 to 30 minutes and fully in about 40, and white light resets them. A trick: look slightly to one side of something faint, since the edge of your vision is more sensitive in the dark.',
      },
      {
        look: (c) => c.firstUp([{ con: 'Ori' }, { con: 'Cas' }, { con: 'Cyg' }, { con: 'UMa' }]) ?? { con: 'UMa' },
        fov: 50,
        title: 'Count the stars',
        text: 'Try counting the stars you can see in this constellation. Seeing only its brightest few means lots of light pollution; dozens means a dark sky. Ahillah’s Where to go finds darker public places nearby.',
      },
    ],
  },
  {
    id: 'triangle',
    title: 'The Summer Triangle',
    level: 'Explorer',
    minutes: 3,
    summary: 'Three bright stars, three constellations and the Milky Way. Best on summer and autumn evenings.',
    north: true,
    steps: [
      {
        look: { star: 'Vega' },
        fov: 70,
        title: 'Three bright stars',
        text: (c) => `Vega, Deneb and Altair make a huge triangle, ${c.where({ star: 'Vega' })} for Vega. Each is in a different constellation: Lyra the Lyre, Cygnus the Swan and Aquila the Eagle. On dark nights, the Milky Way runs right through it.`,
      },
      { find: { star: 'Vega' }, title: 'Find Vega', text: 'Find Vega, the brightest of the three, and put it in the circle.' },
      {
        ask: 'Which of the three is by far the most distant?',
        options: ['Vega', 'Altair', 'Deneb', 'They are all the same distance'],
        answer: 2,
        explain: 'Vega is about 25 light-years away and Altair about 17, but Deneb is well over a thousand. It looks bright only because it is an enormous, brilliant star.',
      },
    ],
  },
  {
    id: 'pleiades',
    title: 'The Pleiades',
    level: 'Everyone',
    minutes: 2,
    summary: 'A cluster of young stars, one of the easiest things to find. Best in the evening from October to March.',
    steps: [
      {
        look: { dso: 'M45' },
        fov: 12,
        title: 'The Pleiades',
        text: (c) =>
          `The Pleiades, al-Thurayyā in Arabic, are ${c.where({ dso: 'M45' })}: a family of young stars about 440 light-years away, born together. Most people see six by eye; binoculars show dozens.`,
      },
      { find: { dso: 'M45' }, title: 'Find them', text: 'Find the little cluster and put it in the circle.' },
      {
        look: { dso: 'M45' },
        fov: 25,
        title: 'Drifting through dust',
        text: () => `In photographs a blue haze surrounds the Pleiades: a cloud of dust they happen to be drifting through.${settings.peek().arabic ? ' In Arabic they are al-Thurayyā.' : ''}`,
      },
      { ask: 'The Pleiades are…', options: ['a galaxy', 'a cluster of young stars', 'a planet and its moons', 'a comet'], answer: 1, explain: 'A star cluster: stars that formed together from the same cloud of gas.' },
    ],
  },
  {
    id: 'jupiter',
    title: 'Jupiter and its moons',
    level: 'Explorer',
    minutes: 2,
    summary: 'The largest planet, and four moons you can see with binoculars.',
    steps: [
      {
        look: { body: 'Jupiter' },
        fov: 30,
        title: 'The largest planet',
        text: (c) => `Jupiter is ${c.where({ body: 'Jupiter' })}. It is more than 11 times wider than Earth and shines brightly and steadily.`,
      },
      {
        look: { body: 'Jupiter' },
        fov: 0.6,
        title: 'Four moons',
        text: 'With binoculars held steady, you can see up to four tiny points lined up beside it: its largest moons, Io, Europa, Ganymede and Callisto. Galileo discovered them in 1610, and their positions change from night to night.',
      },
      {
        ask: 'What do you need to see Jupiter’s four largest moons?',
        options: ['A large observatory', 'Binoculars held steady', 'Nothing: they are easy by eye', 'A space telescope'],
        answer: 1,
        explain: 'Ordinary binoculars show them, especially resting on a wall or a tripod.',
      },
    ],
  },
  {
    id: 'sun',
    title: 'Never look at the Sun',
    level: 'Everyone',
    minutes: 2,
    summary: 'How to watch the Sun and eclipses safely.',
    steps: [
      {
        look: null,
        title: 'Safety first',
        text: 'Looking at the Sun, even briefly, can permanently damage your eyes. Through binoculars or a telescope it can blind you instantly. Sunglasses are not enough.',
      },
      {
        look: null,
        title: 'Safe ways',
        text: (c) =>
          'Use proper eclipse glasses made to the ISO 12312-2 standard, or project the Sun’s image through a pinhole onto card. ' +
          (c.nextSolarEclipse
            ? `The next solar eclipse visible from ${c.placeName} is on ${c.fmtDate(c.nextSolarEclipse.date)}, covering about ${Math.round(c.nextSolarEclipse.obscuration * 100)}% of the Sun.`
            : ''),
      },
      {
        ask: 'Which of these is safe for watching a partial eclipse?',
        options: ['Dark sunglasses', 'A pinhole projector', 'Looking through a phone camera', 'Squinting quickly'],
        answer: 1,
        explain: 'A pinhole projector shows the Sun’s shape on card without you ever looking at it. Certified eclipse glasses are safe too.',
      },
    ],
  },
];

// ---------- True or false ----------
export const FACTS: { s: string; fact: boolean; why: string }[] = [
  { s: 'Polaris is the brightest star in the night sky.', fact: false, why: 'Sirius is the brightest. Polaris is only about the fiftieth brightest; it is famous because it barely moves.' },
  { s: 'Shooting stars are stars falling to Earth.', fact: false, why: 'They are specks of dust burning up high in the air.' },
  { s: 'The Moon has a side that never gets any sunlight.', fact: false, why: 'The far side, most of which we never see, gets as much sunlight as the side facing us.' },
  { s: 'Stars twinkle because the air above us is moving.', fact: true, why: 'Pockets of warmer and cooler air bend starlight about, making it flicker.' },
  { s: 'The Sun is a star.', fact: true, why: 'It is an ordinary star; it looks different only because it is so close.' },
  { s: 'The International Space Station shines with its own lights.', fact: false, why: 'It reflects sunlight, which is why it vanishes when it enters Earth’s shadow.' },
  { s: 'There are 88 official constellations.', fact: true, why: 'Astronomers divide the whole sky into 88 areas.' },
  { s: 'The same side of the Moon always faces Earth.', fact: true, why: 'It spins once each time it goes round us.' },
  { s: 'Stars disappear during the day.', fact: false, why: 'They are still there; sunlight scattered in the air drowns them out.' },
  { s: 'Mars looks reddish because of rusty dust.', fact: true, why: 'Iron oxide, rust, colours its soil and dust.' },
  { s: 'The Plough is one of the 88 constellations.', fact: false, why: 'It is an asterism, a pattern within the constellation Ursa Major.' },
  { s: 'Summer happens because Earth is closer to the Sun.', fact: false, why: 'Earth is closest to the Sun in early January. Seasons come from Earth’s tilt.' },
  { s: 'A light-year measures time.', fact: false, why: 'It is a distance: how far light travels in a year.' },
  { s: 'You need a telescope to see another galaxy.', fact: false, why: 'The Andromeda Galaxy is visible by eye from a dark site.' },
  { s: 'Jupiter’s four largest moons can be seen with binoculars.', fact: true, why: 'Steady binoculars show them as tiny points beside the planet.' },
  { s: 'Venus is sometimes called the evening star.', fact: true, why: 'It is often the first “star” to appear after sunset, though it is a planet.' },
  { s: 'Meteor showers happen when Earth passes through dust left mostly by comets.', fact: true, why: 'Comets leave dust trails along their orbits, and Earth crosses them each year. A few come from asteroids, like the Geminids from Phaethon.' },
  { s: 'Planets usually twinkle more than stars.', fact: false, why: 'Planets show a tiny disc, so they usually shine more steadily than stars.' },
];
